import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Grid, Icon, Tab, Tabs, CircularProgress } from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftBadge from 'components/SoftBadge';
import SoftInput from 'components/SoftInput';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { partnerService } from 'services/partnerService';
import iaasService from 'services/iaasService';
import saasService from 'services/saasService';

function CustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [services, setServices] = useState({ iaas: [], saas: [] });
  const [editingPrice, setEditingPrice] = useState({ serviceId: null, value: '' });

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        setLoading(true);
        setError(null); // Reset any previous errors
        
        // Prüfen, ob der Benutzer Partner-Status hat
        const isPartnerResponse = await partnerService.isPartner();
        if (!isPartnerResponse) {
          throw new Error('Sie benötigen Partner-Berechtigungen, um auf diese Funktion zuzugreifen');
        }
        
        // Fetch customer details from the customers list
        const allCustomers = await partnerService.getCustomers();
        const customerData = allCustomers.find(c => c.id === parseInt(customerId) || c.id === customerId);
        
        if (!customerData) {
          throw new Error('Kunde nicht gefunden');
        }
        
        setCustomer(customerData);
        
        try {
          // Fetch customer services
          const [iaasServices, saasServices] = await Promise.all([
            partnerService.getCustomerServices('iaas'),
            partnerService.getCustomerServices('saas')
          ]);
          
          // Filter services for this customer
          const customerIaasServices = iaasServices.filter(service => 
            service.user && service.user.id === customerData.id);
          const customerSaasServices = saasServices.filter(service => 
            service.user && service.user.id === customerData.id);
          
          setServices({
            iaas: customerIaasServices,
            saas: customerSaasServices
          });
        } catch (serviceErr) {
          console.error('Fehler beim Laden der Kundenservices:', serviceErr);
          // Wir setzen hier keinen Fehler, damit die Kundendetails trotzdem angezeigt werden
          setServices({ iaas: [], saas: [] });
        }
      } catch (err) {
        console.error('Fehler beim Laden der Kundendetails:', err);
        setError(err.message || 'Fehler beim Laden der Kundendetails');
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchCustomerDetails();
    }
  }, [customerId]);

  const handleSaveEndCustomerPrice = async (service, type) => {
    try {
      const price = editingPrice.value;
      if (type === 'iaas') {
        await iaasService.updateEndCustomerPrice(service.id, price);
      } else {
        await saasService.updateEndCustomerPrice(service.id, price);
      }
      setServices(prev => ({
        ...prev,
        [type]: prev[type].map(s =>
          s.id === service.id
            ? { ...s, endcustomer_price: price !== '' ? parseFloat(price) : null }
            : s
        )
      }));
      setEditingPrice({ serviceId: null, value: '' });
    } catch (error) {
      console.error('Fehler beim Speichern des Endkundenpreises:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBackToList = () => {
    navigate('/partner');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 200px)">
          <CircularProgress color="info" />
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <Card>
            <SoftBox p={3} textAlign="center">
              <SoftTypography variant="h5" color="error">
                {error}
              </SoftTypography>
              <SoftBox mt={3}>
                <SoftButton variant="gradient" color="info" onClick={handleBackToList}>
                  Zurück zur Kundenliste
                </SoftButton>
              </SoftBox>
            </SoftBox>
          </Card>
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <SoftTypography variant="h5" fontWeight="medium">
            Kundendetails
          </SoftTypography>
          <SoftButton variant="outlined" color="info" onClick={handleBackToList}>
            Zurück zur Kundenliste
          </SoftButton>
        </SoftBox>

        {/* Customer Information Card */}
        <Card sx={{ mb: 3 }}>
          <SoftBox p={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SoftBox>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                    {customer.company}
                  </SoftTypography>
                  
                  <SoftBox mb={2}>
                    <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                      Kontaktperson:
                    </SoftTypography>{" "}
                    <SoftTypography variant="button" fontWeight="regular">
                      {customer.Vorname} {customer.Nachname}
                    </SoftTypography>
                  </SoftBox>
                  
                  <SoftBox mb={2}>
                    <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                      E-Mail:
                    </SoftTypography>{" "}
                    <SoftTypography variant="button" fontWeight="regular">
                      {customer.email}
                    </SoftTypography>
                  </SoftBox>
                  
                  {customer.phone && (
                    <SoftBox mb={2}>
                      <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                        Telefon:
                      </SoftTypography>{" "}
                      <SoftTypography variant="button" fontWeight="regular">
                        {customer.phone}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <SoftBox>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                    Rechnungsinformationen
                  </SoftTypography>
                  
                  {customer.billingemail && (
                    <SoftBox mb={2}>
                      <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                        Rechnungs-E-Mail:
                      </SoftTypography>{" "}
                      <SoftTypography variant="button" fontWeight="regular">
                        {customer.billingemail}
                      </SoftTypography>
                    </SoftBox>
                  )}
                  
                  {customer.billingaddress && (
                    <SoftBox mb={2}>
                      <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                        Adresse:
                      </SoftTypography>{" "}
                      <SoftTypography variant="button" fontWeight="regular">
                        {customer.billingaddress}
                        {customer.billingzip && customer.billingcity && (
                          <>, {customer.billingzip} {customer.billingcity}</>
                        )}
                        {customer.billingcountry && (
                          <>, {customer.billingcountry}</>
                        )}
                      </SoftTypography>
                    </SoftBox>
                  )}
                  
                  <SoftBox mb={2}>
                    <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                      Status:
                    </SoftTypography>{" "}
                    <SoftBadge
                      variant="contained"
                      color={customer.istactive ? "success" : "error"}
                      size="xs"
                    >
                      {customer.istactive ? "Aktiv" : "Inaktiv"}
                    </SoftBadge>
                  </SoftBox>
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
        </Card>

        {/* Services Tabs */}
        <Card>
          <SoftBox p={3}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab label="Übersicht" />
              <Tab label="IaaS Services" />
              <Tab label="SaaS Services" />
            </Tabs>
            
            <SoftBox mt={3}>
              {/* Overview Tab */}
              {activeTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6} lg={3}>
                    <Card>
                      <SoftBox p={3}>
                        <SoftBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <SoftBox>
                            <SoftTypography variant="h4" fontWeight="medium">
                              {services.iaas.length}
                            </SoftTypography>
                            <SoftTypography variant="button" color="text" fontWeight="regular">
                              IaaS Services
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox
                            width="45px"
                            height="45px"
                            backgroundGradient="info"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            borderRadius="xl"
                          >
                            <Icon fontSize="medium" color="white">
                              memory
                            </Icon>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6} lg={3}>
                    <Card>
                      <SoftBox p={3}>
                        <SoftBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <SoftBox>
                            <SoftTypography variant="h4" fontWeight="medium">
                              {services.saas.length}
                            </SoftTypography>
                            <SoftTypography variant="button" color="text" fontWeight="regular">
                              SaaS Services
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox
                            width="45px"
                            height="45px"
                            backgroundGradient="success"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            borderRadius="xl"
                          >
                            <Icon fontSize="medium" color="white">
                              cloud
                            </Icon>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6} lg={3}>
                    <Card>
                      <SoftBox p={3}>
                        <SoftBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <SoftBox>
                            <SoftTypography variant="h4" fontWeight="medium">
                              {(services.iaas.reduce((sum, service) => sum + parseFloat(service.price || 0), 0) + 
                                services.saas.reduce((sum, service) => sum + parseFloat(service.sprice || 0), 0)).toFixed(2)} €
                            </SoftTypography>
                            <SoftTypography variant="button" color="text" fontWeight="regular">
                              Monatlicher Umsatz
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox
                            width="45px"
                            height="45px"
                            backgroundGradient="dark"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            borderRadius="xl"
                          >
                            <Icon fontSize="medium" color="white">
                              euro
                            </Icon>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {/* IaaS Services Tab */}
              {activeTab === 1 && (
                <SoftBox>
                  {services.iaas.length > 0 ? (
                    <Grid container spacing={3}>
                      {services.iaas.map((service) => (
                        <Grid item xs={12} md={6} lg={4} key={service.id}>
                          <Card>
                            <SoftBox p={3}>
                              <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                              >
                                <SoftBox display="flex" alignItems="center">
                                  <SoftBox
                                    width="40px"
                                    height="40px"
                                    bgColor="info"
                                    variant="gradient"
                                    borderRadius="lg"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    mr={2}
                                  >
                                    <Icon fontSize="small" sx={{ color: 'white' }}>
                                      {service.service_type === 'windows-server' ? 'computer' : 
                                       service.service_type === 'linux-server' ? 'terminal' : 'memory'}
                                    </Icon>
                                  </SoftBox>
                                  <SoftBox>
                                    <SoftTypography variant="h6" fontWeight="medium">
                                      {service.servicename}
                                    </SoftTypography>
                                    <SoftTypography variant="button" color="text">
                                      {service.hostname}
                                    </SoftTypography>
                                  </SoftBox>
                                </SoftBox>
                                <SoftBadge
                                  variant="contained"
                                  color={service.sstatus === 'active' ? 'success' : 
                                         service.sstatus === 'pending' ? 'warning' : 'error'}
                                  size="xs"
                                >
                                  {service.sstatus === 'active' ? 'Aktiv' : 
                                   service.sstatus === 'pending' ? 'In Bearbeitung' : 'Inaktiv'}
                                </SoftBadge>
                              </SoftBox>
                              
                              <SoftBox>
                                <SoftBox mb={0.5}>
                                  <SoftTypography variant="caption" color="text" fontWeight="medium">Einkaufspreis: </SoftTypography>
                                  <SoftTypography component="span" variant="caption" fontWeight="bold">
                                    {parseFloat(service.price || 0).toFixed(2)} €/Monat
                                  </SoftTypography>
                                </SoftBox>
                                <SoftBox display="flex" alignItems="center" mb={1.5} style={{ gap: 4, flexWrap: 'wrap' }}>
                                  <SoftTypography variant="caption" color="text" fontWeight="medium">Endkundenpreis:</SoftTypography>
                                  {editingPrice.serviceId === service.id ? (
                                    <>
                                      <SoftBox style={{ maxWidth: 90 }}>
                                        <SoftInput
                                          type="number"
                                          size="small"
                                          value={editingPrice.value}
                                          onChange={(e) => setEditingPrice(prev => ({ ...prev, value: e.target.value }))}
                                          inputProps={{ min: 0, step: 0.01 }}
                                        />
                                      </SoftBox>
                                      <SoftTypography variant="caption">€</SoftTypography>
                                      <SoftButton variant="gradient" color="success" size="small" onClick={() => handleSaveEndCustomerPrice(service, 'iaas')}>✓</SoftButton>
                                      <SoftButton variant="outlined" color="secondary" size="small" onClick={() => setEditingPrice({ serviceId: null, value: '' })}>✗</SoftButton>
                                    </>
                                  ) : (
                                    <>
                                      <SoftTypography component="span" variant="caption" fontWeight="bold">
                                        {service.endcustomer_price ? `${parseFloat(service.endcustomer_price).toFixed(2)} €/Monat` : '–'}
                                      </SoftTypography>
                                      <Icon
                                        style={{ fontSize: 14, cursor: 'pointer', color: '#aaa', marginLeft: 2 }}
                                        onClick={() => setEditingPrice({ serviceId: service.id, value: service.endcustomer_price ? parseFloat(service.endcustomer_price).toFixed(2) : '' })}
                                      >
                                        edit
                                      </Icon>
                                    </>
                                  )}
                                </SoftBox>
                                <SoftButton
                                  variant="gradient"
                                  color="info"
                                  size="small"
                                  onClick={() => navigate(`/iaas/server/detail/${service.id}`)}
                                >
                                  Service anzeigen
                                </SoftButton>
                              </SoftBox>
                            </SoftBox>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <SoftBox py={5} textAlign="center">
                      <SoftTypography variant="body2" color="text">
                        Keine IaaS Services für diesen Kunden gefunden
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              )}
              
              {/* SaaS Services Tab */}
              {activeTab === 2 && (
                <SoftBox>
                  {services.saas.length > 0 ? (
                    <Grid container spacing={3}>
                      {services.saas.map((service) => (
                        <Grid item xs={12} md={6} lg={4} key={service.id}>
                          <Card>
                            <SoftBox p={3}>
                              <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                              >
                                <SoftBox display="flex" alignItems="center">
                                  <SoftBox
                                    width="40px"
                                    height="40px"
                                    bgColor="success"
                                    variant="gradient"
                                    borderRadius="lg"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    mr={2}
                                  >
                                    <Icon fontSize="small" sx={{ color: 'white' }}>
                                      {service.producttype === 'office-365' ? 'article' : 
                                       service.producttype === 'backup-now' ? 'backup' : 
                                       service.producttype === 'eset' ? 'security' : 'apps'}
                                    </Icon>
                                  </SoftBox>
                                  <SoftBox>
                                    <SoftTypography variant="h6" fontWeight="medium">
                                      {service.servicename}
                                    </SoftTypography>
                                    <SoftTypography variant="button" color="text">
                                      {service.hostname}
                                    </SoftTypography>
                                  </SoftBox>
                                </SoftBox>
                                <SoftBadge
                                  variant="contained"
                                  color={service.sstatus === 'active' ? 'success' : 
                                         service.sstatus === 'pending' ? 'warning' : 'error'}
                                  size="xs"
                                >
                                  {service.sstatus === 'active' ? 'Aktiv' : 
                                   service.sstatus === 'pending' ? 'In Bearbeitung' : 'Inaktiv'}
                                </SoftBadge>
                              </SoftBox>
                              
                              <SoftBox>
                                <SoftBox mb={0.5}>
                                  <SoftTypography variant="caption" color="text" fontWeight="medium">Einkaufspreis: </SoftTypography>
                                  <SoftTypography component="span" variant="caption" fontWeight="bold">
                                    {parseFloat(service.sprice || 0).toFixed(2)} €/Monat
                                  </SoftTypography>
                                </SoftBox>
                                <SoftBox display="flex" alignItems="center" mb={1.5} style={{ gap: 4, flexWrap: 'wrap' }}>
                                  <SoftTypography variant="caption" color="text" fontWeight="medium">Endkundenpreis:</SoftTypography>
                                  {editingPrice.serviceId === service.id ? (
                                    <>
                                      <SoftBox style={{ maxWidth: 90 }}>
                                        <SoftInput
                                          type="number"
                                          size="small"
                                          value={editingPrice.value}
                                          onChange={(e) => setEditingPrice(prev => ({ ...prev, value: e.target.value }))}
                                          inputProps={{ min: 0, step: 0.01 }}
                                        />
                                      </SoftBox>
                                      <SoftTypography variant="caption">€</SoftTypography>
                                      <SoftButton variant="gradient" color="success" size="small" onClick={() => handleSaveEndCustomerPrice(service, 'saas')}>✓</SoftButton>
                                      <SoftButton variant="outlined" color="secondary" size="small" onClick={() => setEditingPrice({ serviceId: null, value: '' })}>✗</SoftButton>
                                    </>
                                  ) : (
                                    <>
                                      <SoftTypography component="span" variant="caption" fontWeight="bold">
                                        {service.endcustomer_price ? `${parseFloat(service.endcustomer_price).toFixed(2)} €/Monat` : '–'}
                                      </SoftTypography>
                                      <Icon
                                        style={{ fontSize: 14, cursor: 'pointer', color: '#aaa', marginLeft: 2 }}
                                        onClick={() => setEditingPrice({ serviceId: service.id, value: service.endcustomer_price ? parseFloat(service.endcustomer_price).toFixed(2) : '' })}
                                      >
                                        edit
                                      </Icon>
                                    </>
                                  )}
                                </SoftBox>
                                <SoftButton
                                  variant="gradient"
                                  color="success"
                                  size="small"
                                  onClick={() => navigate(`/saas/detail/${service.id}`)}
                                >
                                  Service anzeigen
                                </SoftButton>
                              </SoftBox>
                            </SoftBox>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <SoftBox py={5} textAlign="center">
                      <SoftTypography variant="body2" color="text">
                        Keine SaaS Services für diesen Kunden gefunden
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              )}
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CustomerDetail;